import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, errorMsg, nombreModelo, modeloIdDe, pedidoDeRepuesto, ESTADO_PEDIDO_LABEL, fmtFechaCorta } from "@/lib/api";
import { aplicarLimitePorTrabajo } from "@/lib/maximos";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/Panel";
import { numTrabajo } from "@/pages/TrabajosActivos";
import { SugerenciasIADialog } from "@/components/SugerenciasIADialog";
import { PlantillaRepuestosDialog } from "@/components/PlantillaRepuestosDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Minus, Plus, ChevronDown, ChevronRight, Sparkles, PackageCheck, User, Truck } from "lucide-react";
import { EstadoSwitch } from "@/components/EstadoSwitch";
import { EliminarConConfirmacion } from "@/components/EliminarConConfirmacion";
import { useAuth } from "@/lib/auth";

const PRIORIDADES = [
  { value: "alta", label: "Alta", on: "bg-pink-600 text-white", off: "text-pink-600 border-pink-300 hover:bg-pink-50" },
  { value: "media", label: "Media", on: "bg-amber-500 text-white", off: "text-amber-600 border-amber-300 hover:bg-amber-50" },
  { value: "baja", label: "Baja", on: "bg-slate-500 text-white", off: "text-slate-500 border-slate-300 hover:bg-slate-50" },
];

export default function TrabajoSolicitud() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tienePermiso } = useAuth();
  const [trabajo, setTrabajo] = useState(null);
  const [servicios, setServicios] = useState([]);
  const [solicitudes, setSolicitudes] = useState([]);
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [todosModelos, setTodosModelos] = useState(false);
  const [filtroAplicado, setFiltroAplicado] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [pendientesAnt, setPendientesAnt] = useState([]);
  const [iaOpen, setIaOpen] = useState(false);
  const [dupConfirm, setDupConfirm] = useState(null);
  const [eliminados, setEliminados] = useState([]);
  const [pedidoEspecialOpen, setPedidoEspecialOpen] = useState(false);
  const [seleccionPedido, setSeleccionPedido] = useState([]);
  const boxRef = useRef(null);

  // Etiqueta del origen donde ya está incluido un repuesto (para el aviso de duplicado).
  const etiquetaOrigen = (s) => {
    if (s?.origen?.startsWith("plantilla:")) {
      const nom = servicios.find((x) => x.id === s.origen.split(":")[1])?.nombre || "un servicio";
      return `el servicio "${nom}"`;
    }
    if (s?.origen === "ingreso") return "el ingreso del trabajo";
    return "los repuestos agregados manualmente";
  };

  // Nombre corto del origen de OTRA fila con el mismo repuesto (para el aviso discreto
  // "También en <servicio>" en filas duplicadas que no están marcadas "Ya incluido").
  const otroOrigenMismoRepuesto = (s, idx) => {
    const otro = solicitudes.find((x, j) => j !== idx && x.tipo === "repuesto" && x.ref_id === s.ref_id);
    if (!otro) return null;
    if (otro.origen?.startsWith("plantilla:") || otro.origen?.startsWith("ingreso:")) {
      return servicios.find((x) => x.id === otro.origen.split(":")[1])?.nombre || "un servicio";
    }
    if (otro.origen === "ingreso") return "el ingreso del trabajo";
    return "Manual";
  };

  const pushManualRepuesto = (refId, codigo, nombre, categoria) =>
    setSolicitudes((prev) => [...prev, { id: null, tipo: "repuesto", ref_id: refId, codigo: codigo || "",
      nombre, categoria: categoria || "", cantidad: 1, prioridad: "media", estado: "pendiente",
      origen: "manual", plantilla_id: null, mano_obra: null, aportado_cliente: false, ya_incluido: false }]);

  // Agregar un repuesto manual: si ya existe (normal) pide confirmación; si no, lo agrega directo.
  const intentarAgregarRepuesto = (refId, codigo, nombre, categoria) => {
    const existente = solicitudes.find((s) => s.tipo === "repuesto" && s.ref_id === refId && !s.ya_incluido);
    if (existente) {
      setDupConfirm({ refId, codigo, nombre, categoria, origen: etiquetaOrigen(existente) });
      return;
    }
    pushManualRepuesto(refId, codigo, nombre, categoria);
  };

  const agregarRepuestoCatalogo = (rep) =>
    intentarAgregarRepuesto(rep.id, rep.codigo_parte, rep.nombre, rep.categoria || "");

  const repuestosDisponibles = useMemo(() => solicitudes.filter((s) => s.tipo === "repuesto"), [solicitudes]);
  const togglePedidoSel = (refId) => setSeleccionPedido((prev) =>
    prev.includes(refId) ? prev.filter((x) => x !== refId) : [...prev, refId]);
  const abrirPedidoEspecial = () => { setSeleccionPedido([]); setPedidoEspecialOpen(true); };
  const continuarPedidoEspecial = async () => {
    const repuestosPre = repuestosDisponibles.filter((s) => seleccionPedido.includes(s.ref_id))
      .map((s) => ({ ref_id: s.ref_id, codigo: s.codigo, nombre: s.nombre, cantidad: s.cantidad }));
    const ok = await guardar();
    if (!ok) return;
    setPedidoEspecialOpen(false);
    navigate("/pedidos-especiales/nuevo", { state: {
      trabajoId: id, clienteId: trabajo?.cliente?.id, vehiculoId: trabajo?.vehiculo?.id, repuestosPre } });
  };

  const cargarPendientesAnt = () =>
    api.get(`/trabajos/${id}/pendientes-anteriores`).then((r) => setPendientesAnt(r.data)).catch(() => {});

  useEffect(() => {
    (async () => {
      const [t, s] = await Promise.all([
        api.post(`/trabajos/${id}/solicitudes/sincronizar`).catch(() => api.get(`/trabajos/${id}`)),
        api.get("/servicios"),
      ]);
      setTrabajo(t.data);
      setSolicitudes(t.data.solicitudes || []);
      setServicios(s.data);
      cargarPendientesAnt();
    })();
  }, [id]);

  const resolverPendiente = async (item, accion) => {
    try {
      const { data } = await api.post(`/trabajos/${id}/pendientes-anteriores/resolver`, {
        trabajo_origen_id: item.trabajo_origen_id, solicitud_id: item.solicitud.id, accion,
      });
      toast.success(data.mensaje);
      const { data: t } = await api.get(`/trabajos/${id}`);
      setTrabajo(t);
      setSolicitudes(t.solicitudes || []);
      cargarPendientesAnt();
    } catch (err) { toast.error(errorMsg(err)); }
  };

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResultados([]); return; }
    const t = setTimeout(async () => {
      try {
        const params = { q: term, limit: 25 };
        if (modeloIdDe(trabajo)) {
          params.modelo_id = modeloIdDe(trabajo);
          params.todos_modelos = todosModelos;
        }
        const { data, headers } = await api.get("/servicios-buscar", { params });
        setResultados(data);
        setFiltroAplicado(headers?.["x-filtro-aplicado"] === "1");
      } catch { setResultados([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, todosModelos, trabajo]);

  useEffect(() => {
    const fn = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const yaExiste = (tipo, refId) => solicitudes.some((s) => s.tipo === tipo && s.ref_id === refId);

  const agregar = async (item) => {
    setAbierto(false); setQ("");
    if (item.tipo !== "servicio") {
      intentarAgregarRepuesto(item.ref_id, item.codigo, item.nombre, item.categoria);
      return;
    }
    let plantilla = null;
    try {
      const { data } = await api.get(`/servicios/${item.ref_id}/plantilla-resuelta`, {
        params: modeloIdDe(trabajo) ? { modelo_id: modeloIdDe(trabajo) } : {},
      });
      plantilla = data.plantilla;
      if (data.aviso === "sin_plantilla_modelo") toast.info("No hay plantilla para este modelo; se agregó el servicio sin repuestos");
      if (data.aviso === "varias_plantillas") toast.info("Hay varias plantillas para este modelo; se usó la primera. Revise.");
      if (data.aviso_tarifa === "sin_grupo_tarifa") toast.info("El modelo no tiene grupo tarifario; no se puede calcular la mano de obra por FRT");
    } catch { /* servicio sin plantilla */ }
    const items = plantilla?.items || [];
    if (solicitudes.some((s) => s.tipo === "servicio" && s.ref_id === item.ref_id)) return;
    const lista = [...solicitudes];
    lista.push({ id: null, tipo: "servicio", ref_id: item.ref_id, codigo: item.codigo, nombre: item.nombre,
      categoria: "", cantidad: 1, prioridad: "media", estado: "pendiente", origen: "manual",
      plantilla_id: plantilla?.id || null, mano_obra: plantilla ? Number(plantilla.mano_de_obra || 0) : null,
      aportado_cliente: false, ya_incluido: false });
    // Repuestos de la plantilla: entran PENDIENTE (la aprobación se hace en la pantalla de
    // Aprobación). "Lo traemos / Lo trae el cliente" solo indica quién aporta. Si un repuesto
    // ya está en el trabajo y entre ambos superan su "cantidad_maxima" (tope del catálogo vía
    // la plantilla), la línea que excede queda "Ya incluido · Máximo N por trabajo"; sin tope
    // declarado entra siempre normal (son usos distintos y ambos se cobran). Un KIT ya presente
    // excluye a sus componentes ("Incluido en <kit>"), y un segundo kit igual también se excluye.
    const existentesFijas = solicitudes.filter((s) => s.tipo === "repuesto").map((s) => ({ ...s, _fijarDecision: true }));
    const nuevasLineas = items.map((it) => ({ ref_id: it.repuesto_id, nombre: it.nombre, cantidad: it.cantidad || 1,
      cantidad_maxima: it.cantidad_maxima ?? null, ya_incluido: false }));
    const ridsLote = [...new Set([...existentesFijas, ...nuevasLineas].map((l) => l.ref_id).filter(Boolean))];
    const componentesPorKit = ridsLote.length
      ? (await api.post("/repuestos/componentes", { ids: ridsLote }).catch(() => ({ data: {} }))).data
      : {};
    const decididas = aplicarLimitePorTrabajo([...existentesFijas, ...nuevasLineas], componentesPorKit).slice(existentesFijas.length);
    items.forEach((it, idx) => lista.push({ id: null, tipo: "repuesto", ref_id: it.repuesto_id, codigo: it.codigo,
      nombre: it.nombre, categoria: it.categoria, cantidad: it.cantidad || 1, prioridad: "media",
      estado: "pendiente", origen: `plantilla:${item.ref_id}`, plantilla_id: plantilla.id, mano_obra: null,
      aportado_cliente: false, ya_incluido: decididas[idx].ya_incluido, opcional: !!it.opcional,
      cantidad_maxima: it.cantidad_maxima ?? null, maximo_excedido: decididas[idx].maximo_excedido ?? null,
      incluido_en_kit: decididas[idx].incluido_en_kit ?? null }));
    setSolicitudes(lista);
    if (items.length) setPlantillaModal({ servicioId: item.ref_id, servicioNombre: item.nombre,
      items: items.map((it) => ({ ref_id: it.repuesto_id, codigo: it.codigo, nombre: it.nombre,
        categoria: it.categoria, cantidad: it.cantidad || 1, aportado_cliente: false,
        opcional: !!it.opcional, cantidad_maxima: it.cantidad_maxima ?? null })) });
  };

  // Auto-revert: un repuesto "Ya incluido" vuelve a normal si ya no existe otro repuesto normal
  // con el mismo ref_id (p. ej. se eliminó el servicio o el repuesto suelto que lo duplicaba).
  useEffect(() => {
    setSolicitudes((prev) => {
      const normalRefs = new Set(prev.filter((s) => s.tipo === "repuesto" && !s.ya_incluido).map((s) => s.ref_id));
      let cambio = false;
      const next = prev.map((s) => {
        if (s.tipo === "repuesto" && s.ya_incluido && !normalRefs.has(s.ref_id)) {
          cambio = true;
          return { ...s, ya_incluido: false };
        }
        return s;
      });
      return cambio ? next : prev;
    });
  }, [solicitudes]);

  const toggleYaIncluido = (idx) =>
    setSolicitudes((prev) => prev.map((s, i) => (i === idx ? { ...s, ya_incluido: !s.ya_incluido } : s)));

  // Eliminar una fila. Un servicio arrastra sus repuestos heredados (origen plantilla:<id>) y,
  // si es el principal de un cronograma, también sus servicios generados (origen ingreso:<id>)
  // junto con los repuestos de esos generados.
  // eliminar_ids: se acumulan los ids de líneas quitadas (si ya existían) para forzar su
  // borrado al Guardar, aunque estén aprobadas/rechazadas (ver guardar_solicitudes backend).
  const eliminar = (idx) => {
    const s = solicitudes[idx];
    if (!s) return;
    if (s.tipo === "servicio") {
      const pref = `plantilla:${s.ref_id}`;
      const generados = solicitudes.filter((x) => x.tipo === "servicio" && x.origen === `ingreso:${s.ref_id}`);
      const refsFuera = new Set([s.ref_id, ...generados.map((g) => g.ref_id)]);
      const quedan = solicitudes.filter((x, j) => {
        if (j === idx) return false;
        if (x.origen === pref) return false;
        if (x.tipo === "servicio" && refsFuera.has(x.ref_id)) return false;
        if (x.tipo === "repuesto" && x.origen?.startsWith("plantilla:") && refsFuera.has(x.origen.split(":")[1])) return false;
        return true;
      });
      const idsQuitados = solicitudes.filter((x) => !quedan.includes(x)).map((x) => x.id).filter(Boolean);
      if (idsQuitados.length) setEliminados((prev) => [...prev, ...idsQuitados]);
      setSolicitudes(quedan);
      return;
    }
    let next = solicitudes.filter((_, j) => j !== idx);
    const idsQuitados = s.id ? [s.id] : [];
    const org = s.origen || "";
    if (org.startsWith("plantilla:")) {
      const sid = org.split(":")[1];
      const quedan = next.some((x) => x.tipo === "repuesto" && x.origen === `plantilla:${sid}`);
      if (!quedan) {
        const padre = next.find((x) => x.tipo === "servicio" && x.ref_id === sid);
        if (padre) {
          toast.info(`Se quitó el servicio "${padre.nombre}" porque no le quedan repuestos`);
          if (padre.id) idsQuitados.push(padre.id);
          next = next.filter((x) => !(x.tipo === "servicio" && x.ref_id === sid));
        }
      }
    }
    if (idsQuitados.length) setEliminados((prev) => [...prev, ...idsQuitados]);
    setSolicitudes(next);
  };

  const [plantillaModal, setPlantillaModal] = useState(null);
  const reabrirPlantilla = (servicioId, servicioNombre) => {
    const items = solicitudes.filter((s) => s.tipo === "repuesto" && s.origen === `plantilla:${servicioId}`)
      .map((s) => ({ ref_id: s.ref_id, codigo: s.codigo, nombre: s.nombre, categoria: s.categoria,
        cantidad: s.cantidad, aportado_cliente: !!s.aportado_cliente, opcional: !!s.opcional,
        cantidad_maxima: s.cantidad_maxima ?? null }));
    setPlantillaModal({ servicioId, servicioNombre, items });
  };
  const confirmarPlantilla = async (reps) => {
    const sid = plantillaModal.servicioId;
    const exPorRef = {};
    solicitudes.forEach((s) => { if (s.tipo === "repuesto" && s.origen === `plantilla:${sid}`) exPorRef[s.ref_id] = s; });
    const sinEsta = solicitudes.filter((s) => !(s.tipo === "repuesto" && s.origen === `plantilla:${sid}`));
    // Las líneas que ya existían conservan su decisión (manual o automática) tal cual; solo las
    // nuevas (agregadas/editadas en el diálogo) se deciden contra el tope "cantidad_maxima"/kits.
    const otrasFijas = sinEsta.filter((s) => s.tipo === "repuesto").map((s) => ({ ...s, _fijarDecision: true }));
    const lineasReps = reps.map((r) => {
      const ex = exPorRef[r.ref_id];
      return { ref_id: r.ref_id, nombre: r.nombre, cantidad: r.cantidad || 1,
        cantidad_maxima: r.cantidad_maxima ?? ex?.cantidad_maxima ?? null,
        ya_incluido: ex ? !!ex.ya_incluido : false, maximo_excedido: ex?.maximo_excedido ?? null,
        _fijarDecision: !!ex };
    });
    const ridsLote = [...new Set([...otrasFijas, ...lineasReps].map((l) => l.ref_id).filter(Boolean))];
    const componentesPorKit = ridsLote.length
      ? (await api.post("/repuestos/componentes", { ids: ridsLote }).catch(() => ({ data: {} }))).data
      : {};
    const decididas = aplicarLimitePorTrabajo([...otrasFijas, ...lineasReps], componentesPorKit).slice(otrasFijas.length);
    const nuevos = reps.map((r, idx) => {
      const ex = exPorRef[r.ref_id];
      return { id: ex?.id || null, tipo: "repuesto", ref_id: r.ref_id, codigo: r.codigo || "",
        nombre: r.nombre, categoria: r.categoria || "", cantidad: r.cantidad || 1, prioridad: "media",
        estado: ex?.estado || "pendiente", origen: `plantilla:${sid}`, plantilla_id: ex?.plantilla_id || null,
        mano_obra: null, aportado_cliente: !!r.aportado_cliente,
        ya_incluido: decididas[idx].ya_incluido, opcional: !!r.opcional,
        cantidad_maxima: decididas[idx].cantidad_maxima ?? null,
        maximo_excedido: decididas[idx].maximo_excedido ?? null,
        incluido_en_kit: decididas[idx].incluido_en_kit ?? null };
    });
    setSolicitudes([...sinEsta, ...nuevos]);
  };

  const setSol = (idx, campo, valor) =>
    setSolicitudes((prev) => prev.map((s, i) => (i === idx ? { ...s, [campo]: valor } : s)));

  const guardar = async () => {
    setGuardando(true);
    try {
      const { data } = await api.put(`/trabajos/${id}/solicitudes`, {
        solicitudes: solicitudes.map(({ id: sid, tipo, ref_id, codigo, nombre, categoria, cantidad, prioridad, origen, plantilla_id, mano_obra, estado, aportado_cliente, ya_incluido, opcional, cantidad_maxima, maximo_excedido, incluido_en_kit }) =>
          ({ id: sid, tipo, ref_id, codigo, nombre, categoria, cantidad, prioridad, origen, plantilla_id, mano_obra, estado, aportado_cliente, ya_incluido, opcional, cantidad_maxima, maximo_excedido, incluido_en_kit })),
        eliminar_ids: eliminados,
      });
      setSolicitudes(data.solicitudes || []);
      setEliminados([]);
      toast.success("Solicitud guardada de forma exitosa");
      return true;
    } catch (e) { toast.error(errorMsg(e)); return false; }
    finally { setGuardando(false); }
  };

  const origenServicio = (s) => {
    if (s.origen === "ingreso" || s.origen?.startsWith("ingreso:")) return "del ingreso del trabajo";
    if (!s.origen?.startsWith("plantilla:")) return null;
    const nom = servicios.find((x) => x.id === s.origen.split(":")[1])?.nombre || "plan";
    return `de plantilla: ${nom}`;
  };

  const [colapsados, setColapsados] = useState({});

  // Conteo de repuestos por ref_id: define cuándo mostrar el toggle de "Ya incluido".
  const refCount = useMemo(() => {
    const m = {};
    solicitudes.forEach((s) => { if (s.tipo === "repuesto") m[s.ref_id] = (m[s.ref_id] || 0) + 1; });
    return m;
  }, [solicitudes]);

  const filasAgrupadas = useMemo(() => {
    const presentes = new Set(solicitudes.filter((s) => s.tipo === "servicio").map((s) => s.ref_id));
    const hijosDe = {};
    solicitudes.forEach((s, i) => {
      const m = (s.origen || "").match(/^plantilla:(.+)$/);
      if (m && presentes.has(m[1])) (hijosDe[m[1]] = hijosDe[m[1]] || []).push({ s, i });
    });
    const enGrupo = new Set(Object.values(hijosDe).flat().map((x) => x.i));
    const filas = [];
    solicitudes.forEach((s, i) => {
      if (enGrupo.has(i)) return;
      const hijos = s.tipo === "servicio" ? hijosDe[s.ref_id] || [] : [];
      if (hijos.length) {
        filas.push({ s, i, rol: "padre", nHijos: hijos.length });
        if (!colapsados[s.ref_id]) hijos.forEach((h) => filas.push({ ...h, rol: "hijo", padreNombre: s.nombre }));
      } else {
        filas.push({ s, i, rol: "suelto" });
      }
    });
    return filas;
  }, [solicitudes, colapsados]);

  // Agrupa las filas renderizadas (padre+hijos, o sueltas) para que cada grupo viva en su
  // propio <tbody>, con una fila separadora de 12px entre grupos.
  const gruposRender = useMemo(() => {
    const grupos = [];
    let actual = null;
    filasAgrupadas.forEach((f) => {
      if (f.rol === "padre") {
        actual = { filas: [f] };
        grupos.push(actual);
      } else if (f.rol === "hijo" && actual) {
        actual.filas.push(f);
      } else {
        grupos.push({ filas: [f] });
        actual = null;
      }
    });
    grupos.forEach((g) => {
      const ultimo = g.filas[g.filas.length - 1];
      if (ultimo.rol === "hijo") g.filas[g.filas.length - 1] = { ...ultimo, esUltimoHijo: true };
    });
    return grupos;
  }, [filasAgrupadas]);

  return (
    <div data-testid="trabajo-solicitud-page" className="space-y-4">
      <Panel titulo={`Solicitud de repuestos y servicios — Trabajo ${trabajo ? numTrabajo(trabajo.numero) : ""}`}
        testId="solicitud-panel">
        {trabajo && (
          <p className="text-sm text-slate-600 mb-4" data-testid="solicitud-info">
            {trabajo.vehiculo?.placa} · {nombreModelo(trabajo.vehiculo?.marca, trabajo.vehiculo?.modelo)} · {trabajo.cliente?.nombre}
          </p>
        )}
        {pendientesAnt.length > 0 && (
          <div data-testid="pendientes-anteriores-panel"
            className="mb-4 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
            <p className="text-sm font-semibold text-amber-800 mb-2">
              Pendientes de trabajos anteriores ({pendientesAnt.length})
            </p>
            {pendientesAnt.map((it, i) => (
              <div key={it.solicitud.id} data-testid={`pendiente-anterior-${i}`}
                className="flex items-center gap-2 py-1.5 border-t border-amber-100 first:border-0 text-sm">
                <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                  it.solicitud.tipo === "servicio" ? "bg-indigo-50 text-indigo-700" : "bg-teal-50 text-teal-700"}`}>
                  {it.solicitud.tipo === "servicio" ? "Servicio" : "Repuesto"}
                </span>
                <span className="truncate text-slate-700">
                  {it.solicitud.nombre}
                  {it.solicitud.tipo === "repuesto" && it.solicitud.cantidad > 1 ? ` x${it.solicitud.cantidad}` : ""}
                </span>
                <span className="shrink-0 text-xs text-slate-400">de Orden #{numTrabajo(it.numero_origen)}</span>
                <div className="ml-auto flex gap-1.5 shrink-0">
                  <Button size="sm" data-testid={`pendiente-anterior-aprobar-${i}`}
                    className="h-7 bg-teal-600 hover:bg-teal-700 text-white"
                    onClick={() => resolverPendiente(it, "aprobar")}>
                    Aprobar
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`pendiente-anterior-rechazar-${i}`}
                    className="h-7 border-pink-300 text-pink-700 hover:bg-pink-50"
                    onClick={() => resolverPendiente(it, "rechazar")}>
                    Rechazar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div ref={boxRef} className="relative max-w-xl">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="solicitud-buscar-input" value={q}
            onChange={(e) => { setQ(e.target.value); setTodosModelos(false); setAbierto(true); }}
            onFocus={() => setAbierto(true)}
            placeholder="Buscar repuesto o servicio..." className="pl-9" />
          {abierto && q.trim() && (resultados.length > 0 || modeloIdDe(trabajo)) && (
            <div data-testid="solicitud-resultados"
              className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-72 overflow-y-auto overscroll-contain">
              {modeloIdDe(trabajo) && !todosModelos && (
                <p data-testid="solicitud-filtro-modelo-hint"
                  className="px-3 py-1.5 text-[11px] text-slate-400 border-b border-slate-50">
                  {filtroAplicado
                    ? `Repuestos filtrados por modelo: ${nombreModelo(trabajo.vehiculo.marca, trabajo.vehiculo.modelo)}`
                    : `Repuestos (primero los del catálogo de ${nombreModelo(trabajo.vehiculo.marca, trabajo.vehiculo.modelo)})`}
                </p>
              )}
              {modeloIdDe(trabajo) && todosModelos && (
                <p data-testid="solicitud-todos-modelos-hint"
                  className="px-3 py-1.5 text-[11px] text-amber-600 border-b border-slate-50">
                  Mostrando repuestos de todos los modelos
                </p>
              )}
              {resultados.map((r, i) => (
                <button key={`${r.tipo}-${r.ref_id}`} type="button" data-testid={`solicitud-resultado-${i}`}
                  onClick={() => agregar(r)} disabled={r.tipo === "servicio" && yaExiste("servicio", r.ref_id)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-40 border-b border-slate-50 last:border-0">
                  <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                    r.tipo === "servicio" ? "bg-indigo-50 text-indigo-700" : "bg-teal-50 text-teal-700"}`}>
                    {r.tipo === "servicio" ? "Servicio" : "Repuesto"}
                  </span>
                  <span className="truncate text-slate-700">{r.nombre}</span>
                  {r.tipo === "repuesto" && (
                    <span className="ml-auto shrink-0 flex items-center gap-1.5 text-xs text-slate-400">
                      {r.codigo}
                      {r.en_catalogo && (
                        <span data-testid={`solicitud-tag-catalogo-${i}`}
                          className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Catálogo
                        </span>
                      )}
                      {!r.en_catalogo && r.compatible && (
                        <span data-testid={`solicitud-tag-compatible-${i}`}
                          className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase bg-amber-50 text-amber-700 border border-amber-200">
                          Compatible
                        </span>
                      )}
                      <span>· {r.categoria || "—"}</span>
                    </span>
                  )}
                  {yaExiste(r.tipo, r.ref_id) && <span className="ml-auto shrink-0 text-xs text-slate-400">{r.tipo === "servicio" ? "ya agregado" : "ya incluido"}</span>}
                </button>
              ))}
              {resultados.length === 0 && (
                <div className="px-3 py-2">
                  <p className="text-sm text-slate-400">Sin resultados para este modelo</p>
                  {modeloIdDe(trabajo) && (
                    <button type="button" data-testid="solicitud-sugerir-ia-btn"
                      onClick={() => { setAbierto(false); setIaOpen(true); }}
                      className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800">
                      <Sparkles size={13} /> Sugerir con IA desde el catálogo de referencia
                    </button>
                  )}
                </div>
              )}
              {modeloIdDe(trabajo) && !todosModelos && (
                <button type="button" data-testid="solicitud-buscar-todos-btn"
                  onClick={() => setTodosModelos(true)}
                  className="w-full px-3 py-2 text-left text-xs font-medium text-indigo-600 hover:bg-indigo-50 border-t border-slate-100">
                  Buscar en todos los modelos
                </button>
              )}
            </div>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" data-testid="solicitud-ia-btn"
            disabled={!modeloIdDe(trabajo)} onClick={() => setIaOpen(true)}
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800">
            <Sparkles size={14} className="mr-1.5" /> Sugerir con IA
            <span className="ml-1.5 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-600">beta</span>
          </Button>
          <span className="text-xs text-slate-400">¿No existe el repuesto? La IA sugiere el código de parte desde el catálogo de referencia Honda</span>
          {tienePermiso("pedidos.crear") && (
            <Button type="button" variant="outline" size="sm" data-testid="solicitud-crear-pedido-especial-btn"
              onClick={abrirPedidoEspecial}
              className="ml-auto border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800">
              <Truck size={14} className="mr-1.5" /> Crear pedido especial
            </Button>
          )}
        </div>
      </Panel>

      {modeloIdDe(trabajo) && (
        <SugerenciasIADialog open={iaOpen} onOpenChange={setIaOpen}
          modeloId={modeloIdDe(trabajo)}
          modeloNombre={nombreModelo(trabajo.vehiculo.marca, trabajo.vehiculo.modelo)}
          pedidoInicial={q}
          onRepuestoListo={agregarRepuestoCatalogo} />
      )}

      <Panel titulo="Trabajos a realizar / Solicitud" testId="solicitud-lista-panel">
        <div className="overflow-x-auto">
          <table data-testid="solicitud-tabla" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="py-2.5 pr-3 pl-4 font-semibold">Estado</th>
                <th className="py-2.5 pr-3 font-semibold">Repuesto / Servicio</th>
                <th className="py-2.5 pr-3 font-semibold">Prioridad</th>
                <th className="py-2.5 pr-4 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            {gruposRender.map((g, gi) => (
              <tbody key={gi}>
                {gi > 0 && (
                  <tr aria-hidden="true">
                    <td colSpan={4} style={{ height: 12, padding: 0, border: "none", background: "transparent" }} />
                  </tr>
                )}
                {g.filas.map(({ s, i, rol, nHijos, esUltimoHijo }) => {
                  const bloqueada = s.estado && s.estado !== "pendiente";
                  const esGrupo = rol === "padre" || rol === "hijo";
                  const claseFila = rol === "padre" ? "bg-[#216295] text-white"
                    : rol === "hijo" ? `bg-white border-x border-slate-300 ${esUltimoHijo ? "border-b border-slate-300" : "border-b border-slate-200"}`
                    : "border-b border-slate-100";
                  const codigoCat = [s.codigo, s.categoria].filter(Boolean).join(" · ");
                  const pedidoEsp = pedidoDeRepuesto(trabajo, s);
                  return (
                    <tr key={s.id || `${s.tipo}-${s.ref_id}`} data-testid={`solicitud-row-${i}`}
                      data-rol={rol} className={claseFila}>
                      <td className="py-2.5 pr-3 pl-4">
                        <EstadoSwitch estado={s.estado || "pendiente"}
                          onDark={rol === "padre"} disabled soloLectura
                          title="El estado se cambia en Aprobación"
                          testId={`solicitud-estado-${i}`} />
                      </td>
                      <td className="py-2.5 pr-3">
                        <div className={rol === "hijo" ? "ml-3 sm:ml-6" : ""}>
                          <div className="flex items-center gap-2">
                            {rol === "padre" && (
                              <button type="button" data-testid={`solicitud-colapsar-${i}`}
                                title={colapsados[s.ref_id] ? "Mostrar repuestos heredados" : "Ocultar repuestos heredados"}
                                onClick={() => setColapsados((c) => ({ ...c, [s.ref_id]: !c[s.ref_id] }))}
                                className="w-5 h-5 flex items-center justify-center rounded text-white hover:bg-white/15">
                                {colapsados[s.ref_id] ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                              </button>
                            )}
                            <span className={`font-medium ${rol === "padre" ? "text-[14px] text-white"
                              : s.ya_incluido ? "text-slate-400 line-through" : "text-slate-800"}`}>{s.nombre}</span>
                            {s.tipo === "repuesto" && s.ya_incluido && (
                              <span data-testid={`solicitud-ya-incluido-badge-${i}`}
                                className="chip rounded-full bg-slate-200 text-slate-500">
                                Ya incluido
                              </span>
                            )}
                            {s.tipo === "repuesto" && s.opcional && (
                              <span data-testid={`solicitud-opcional-badge-${i}`}
                                className="chip shrink-0 rounded-full bg-sky-100 text-sky-700">
                                Opcional
                              </span>
                            )}
                            {rol === "padre" && (
                              <span data-testid={`solicitud-hijos-count-${i}`}
                                className="chip rounded-full bg-white text-[#216295]">
                                {nHijos} repuesto{nHijos > 1 ? "s" : ""}
                              </span>
                            )}
                            {rol === "padre" && (
                              <button type="button" data-testid={`solicitud-reabrir-plantilla-${i}`}
                                onClick={() => reabrirPlantilla(s.ref_id, s.nombre)}
                                className="chip gap-1 rounded-full bg-white text-[#216295] hover:bg-slate-100">
                                <PackageCheck size={12} /> Revisar repuestos
                              </button>
                            )}
                          </div>
                          <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ${rol === "padre" ? "ml-7 text-white/80" : "text-slate-500"}`}>
                            {codigoCat && <span className="text-[12px]">{codigoCat}</span>}
                            {rol !== "hijo" && origenServicio(s) && <span className={rol === "padre" ? "text-white" : "text-teal-600"}>{origenServicio(s)}</span>}
                            {s.tipo === "repuesto" && s.aportado_cliente && (
                              <span data-testid={`solicitud-aportado-cliente-${i}`}
                                className="chip gap-1 rounded bg-amber-100 text-amber-800">
                                <User size={11} /> Lo trae el cliente
                              </span>
                            )}
                            {s.tipo === "repuesto" && pedidoEsp && (
                              <button type="button" data-testid={`solicitud-pedido-especial-chip-${i}`}
                                onClick={() => navigate(`/pedidos-especiales/${pedidoEsp.id}`)}
                                className={`chip gap-1 rounded-full border ${pedidoEsp.atrasado
                                  ? "border-pink-300 bg-pink-50 text-pink-700" : "border-violet-300 bg-violet-50 text-violet-700"}`}>
                                <Truck size={11} /> {pedidoEsp.numero_fmt} · {ESTADO_PEDIDO_LABEL[pedidoEsp.estado]}
                                {pedidoEsp.fecha_estimada_arribo ? ` · llega ${fmtFechaCorta(pedidoEsp.fecha_estimada_arribo)}` : ""}
                              </button>
                            )}
                            {s.tipo === "repuesto" && !s.ya_incluido && refCount[s.ref_id] > 1 && (
                              <span data-testid={`solicitud-tambien-en-${i}`} className="italic text-slate-400">
                                También en {otroOrigenMismoRepuesto(s, i) || "otra línea"}
                              </span>
                            )}
                            {s.tipo === "repuesto" && s.ya_incluido && s.incluido_en_kit && (
                              <span data-testid={`solicitud-incluido-en-kit-${i}`} className="italic text-slate-400">
                                Incluido en {s.incluido_en_kit}
                              </span>
                            )}
                            {s.tipo === "repuesto" && s.ya_incluido && !s.incluido_en_kit && s.maximo_excedido != null && (
                              <span data-testid={`solicitud-maximo-excedido-${i}`} className="italic text-slate-400">
                                Máximo {s.maximo_excedido} por trabajo
                              </span>
                            )}
                            {s.tipo === "repuesto" && (s.ya_incluido || refCount[s.ref_id] > 1) && (
                              <button type="button" data-testid={`solicitud-toggle-incluido-${i}`}
                                onClick={() => toggleYaIncluido(i)}
                                className={`chip gap-1 rounded-full border transition-colors ${
                                  s.ya_incluido ? "border-teal-300 text-teal-700 hover:bg-teal-50"
                                    : "border-slate-300 text-slate-500 hover:bg-slate-50"}`}>
                                {s.ya_incluido ? "Incluir de todos modos" : "Marcar «Ya incluido»"}
                              </button>
                            )}
                            {s.tipo === "repuesto" && (
                              <span className="inline-flex items-center gap-1">
                                Cant.:
                                <button type="button" data-testid={`solicitud-menos-${i}`} disabled={bloqueada || s.cantidad <= 1}
                                  onClick={() => setSol(i, "cantidad", s.cantidad - 1)}
                                  className="w-5 h-5 flex items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-30">
                                  <Minus size={11} />
                                </button>
                                <b data-testid={`solicitud-cantidad-${i}`} className="w-5 text-center text-slate-700">{s.cantidad}</b>
                                <button type="button" data-testid={`solicitud-mas-${i}`} disabled={bloqueada}
                                  onClick={() => setSol(i, "cantidad", s.cantidad + 1)}
                                  className="w-5 h-5 flex items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-30">
                                  <Plus size={11} />
                                </button>
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 pr-3">
                        <div className="flex gap-1">
                          {PRIORIDADES.map((p) => (
                            <button key={p.value} type="button" data-testid={`solicitud-prioridad-${p.value}-${i}`}
                              disabled={bloqueada} onClick={() => setSol(i, "prioridad", p.value)}
                              className={`chip rounded-full border transition-colors disabled:opacity-40 ${
                                s.prioridad === p.value ? `${p.on} border-transparent` : `${esGrupo ? "bg-white/70" : "bg-white"} ${p.off}`}`}>
                              {p.label}
                            </button>
                          ))}
                        </div>
                      </td>
                      <td className="py-2.5 pr-4">
                        <div className="flex justify-end">
                          {tienePermiso("trabajos.borrar_lineas") && (
                          <EliminarConConfirmacion
                            mensaje={s.tipo === "servicio" ? "¿Eliminar el servicio y sus repuestos? No aparecerán en la boleta." : "¿Eliminar esta línea? No aparecerá en la boleta."}
                            onConfirmar={() => eliminar(i)}
                            testId={`solicitud-eliminar-${i}`} />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            ))}
            {solicitudes.length === 0 && (
              <tbody>
                <tr><td colSpan={4} className="py-8 text-center text-slate-400" data-testid="solicitud-vacio">
                  Use el buscador para agregar repuestos o servicios a la solicitud
                </td></tr>
              </tbody>
            )}
          </table>
        </div>
        <div className="flex justify-end mt-6">
          <Button data-testid="solicitud-guardar-btn" onClick={guardar} disabled={guardando}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-8">
            {guardando ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </Panel>

      <PlantillaRepuestosDialog open={!!plantillaModal} onOpenChange={(o) => { if (!o) setPlantillaModal(null); }}
        servicioNombre={plantillaModal?.servicioNombre} items={plantillaModal?.items || []}
        onConfirmar={confirmarPlantilla} />

      <AlertDialog open={!!dupConfirm} onOpenChange={(o) => { if (!o) setDupConfirm(null); }}>
        <AlertDialogContent data-testid="solicitud-dup-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Repuesto ya incluido</AlertDialogTitle>
            <AlertDialogDescription>
              Este repuesto ya está incluido (en {dupConfirm?.origen}). ¿Agregarlo de todos modos?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="solicitud-dup-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="solicitud-dup-agregar"
              onClick={() => {
                if (dupConfirm) pushManualRepuesto(dupConfirm.refId, dupConfirm.codigo, dupConfirm.nombre, dupConfirm.categoria);
                setDupConfirm(null);
              }}>
              Agregar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={pedidoEspecialOpen} onOpenChange={setPedidoEspecialOpen}>
        <DialogContent className="bg-white max-h-[80vh] overflow-y-auto" data-testid="solicitud-pedido-especial-dialog">
          <DialogHeader><DialogTitle>Crear pedido especial</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Marque los repuestos de esta solicitud que se van a pedir a fábrica.</p>
          <p className="text-xs text-amber-600">Los cambios de la solicitud se guardarán antes de continuar. Los repuestos del pedido quedarán aprobados en el trabajo.</p>
          <div className="space-y-1.5">
            {repuestosDisponibles.length === 0 && (
              <p className="text-sm text-slate-400 py-4 text-center">No hay repuestos en esta solicitud</p>
            )}
            {repuestosDisponibles.map((s) => (
              <label key={s.id || s.ref_id} className="flex items-center gap-2 text-sm text-slate-700 py-1">
                <input type="checkbox" data-testid={`solicitud-pedido-especial-check-${s.ref_id}`}
                  checked={seleccionPedido.includes(s.ref_id)} onChange={() => togglePedidoSel(s.ref_id)}
                  className="accent-indigo-600" />
                {s.nombre} {s.cantidad > 1 ? `x${s.cantidad}` : ""}
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPedidoEspecialOpen(false)}>Cancelar</Button>
            <Button data-testid="solicitud-pedido-especial-continuar-btn" onClick={continuarPedidoEspecial} disabled={guardando}>
              {guardando ? "Guardando..." : `Continuar (${seleccionPedido.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
